import {
  FormTemplate,
  FormTemplateFieldMap,
  FormTemplateOption,
  FormTemplateSection,
} from 'data/template/template.types';
import cloneDeep from 'lodash.clonedeep';
import {
  TargetDeliveryTemplateAccessFunction,
  TargetDeliveryTemplateInterfaceFormData,
  TargetDeliveryTemplateAccessFunctionFormData,
  TARGET_DELIVERY_TEMPLATE_TEMPORARY_ID,
  TargetDeliveryTemplateSection,
} from 'data/targetDeliveryTemplateAccessFunction/targetDeliveryTemplateAccessFunction.types';
import { AccessFunction } from 'data/accessFunction/accessFunction.types';
import { SupportedFeaturesAccessFunctions } from 'data/supportedFeatures/supportedFeatures.types';
import { AuditDetails, Operation } from 'data/types';
import {
  AuditActionType,
  AuditService,
  getAuditDetails,
  getAuditFieldInfo,
} from 'shared/userAudit/audit-constants';
import * as _ from 'lodash';
import { GENBAND_TARGET_CCC_ROWS } from '../templates/target-delivery-genband-20-template';
import { EricssonNeTargetTemplateFormSection, TargetTemplateFormSection } from '../TargetDeliveryTemplateAccessFunction.types';

export function adaptTemplateOptions(
  templates: {[key: string]: FormTemplate},
  accessFunctions: AccessFunction[],
  enabledAccessFunctions: SupportedFeaturesAccessFunctions,
): { [key: string]: FormTemplate } {
  const adaptedTemplates: { [key: string]: FormTemplate } = {};

  const enabledTemplates = Object.keys(templates)
    .filter((templateKey) => _.has(enabledAccessFunctions, templateKey))
    .reduce((acc, key) => {
      acc[key] = templates[key];
      return acc;
    }, {} as { [key: string]: FormTemplate });

  const afTypeOptions = getAfTypeTagOptions(enabledTemplates, enabledAccessFunctions);
  const afIdOptions = getAfIdOptions(enabledTemplates, accessFunctions);

  Object.keys(enabledTemplates).forEach((templateName) => {
    const template = cloneDeep(templates[templateName]);

    if (enabledAccessFunctions[template.key] == null) {
      return;
    }

    setFieldOptions(template.main as FormTemplateSection, 'afType', afTypeOptions);
    setFieldOptions(template.main as FormTemplateSection, 'afId', afIdOptions[template.key], true);
    (template.main as FormTemplateSection).fields.afType.initial = afTypeOptions[0].value;

    adaptedTemplates[template.key] = template;
  });

  return adaptedTemplates;
}

export function getInterfaceFormTemplateSection(
  interfaceName: TargetTemplateFormSection,
  data: Partial<TargetDeliveryTemplateInterfaceFormData>,
  template: FormTemplate,
): FormTemplateSection | undefined {
  return template[interfaceName] as FormTemplateSection;
}

export function adaptInterfaceTemplateState(
  interfaceFields: FormTemplateFieldMap,
  interfaceData:Partial<TargetDeliveryTemplateInterfaceFormData>,
  isEditing: boolean,
):FormTemplateFieldMap {

  const clonedInterfaceFields = cloneDeep(interfaceFields);

  if (
    isEditing &&
    clonedInterfaceFields.state != null &&
    interfaceData.id?.includes(TARGET_DELIVERY_TEMPLATE_TEMPORARY_ID)
  ) {
    clonedInterfaceFields.state.hide = 'true';
  }

  return clonedInterfaceFields;
}

export function adaptTemplateVisibilityMode(
  template: FormTemplate | undefined,
  mode: 'create' | 'edit' | 'view' | 'copy',
): FormTemplate | undefined {
  if (template == null) {
    return;
  }

  let clonedTemplate = cloneDeep(template);
  clonedTemplate.formId = Date.now().toString();

  if (mode === 'view') {
    clonedTemplate = adaptTemplateForViewMode(clonedTemplate);

  } else if (mode === 'edit') {
    clonedTemplate = adaptTemplateForEditMode(clonedTemplate);
  }

  return clonedTemplate;
}

export function getTargetTemplateToSubmit(
  data: TargetDeliveryTemplateAccessFunctionFormData,
): TargetDeliveryTemplateAccessFunction {

  return {
    id: data.id,
    name: data.name,
    afId: data.afId,
    afType: data.afType,
    templateSections: [
      ...adaptTargetSectionToBody('targetInformation', data.targetInformation, data),
      ...adaptTargetSectionToBody('callContentChannels', data.callContentChannels, data),
      ...adaptTargetSectionToBody('monitoringCenterFlags', data.monitoringCenterFlags, data),
      ...adaptTargetSectionToBody('ICD', data.ICD, data),
      ...adaptTargetSectionToBody('networkType', data.networkType, data),
      ...adaptTargetSectionToBody('callContentType', data.callContentType, data),
      ...adaptTargetSectionToBody('EIMSMCNB', data.EIMSMCNB, data),
      ...adaptTargetSectionToBody('EIMSTARGET', data.EIMSTARGET, data),
      ...adaptTargetSectionToBody('EIMSMSCTRGT', data.EIMSMSCTRGT, data),
      ...adaptTargetSectionToBody('EIMSGPRSTRGT', data.EIMSGPRSTRGT, data),
      ...adaptTargetSectionToBody('EIMSGENERAL', data.EIMSGENERAL, data),
      ...adaptTargetSectionToBody('EIMSWRLNTRGT', data.EIMSWRLNTRGT, data),
      ...adaptTargetSectionToBody('EIMSMC', data.EIMSMC, data),
      ...adaptTargetSectionToBody('usnbdTargetInformation', data.usnbdTargetInformation, data),
      ...adaptTargetSectionToBody('usnbdCallContentChannelInformation', data.usnbdCallContentChannelInformation, data),
    ],
  };
}

function adaptTargetSectionToBody(
  name: 'targetInformation' | 'callContentChannels' | 'ICD' | 'networkType' | 'callContentType' | 'monitoringCenterFlags' | EricssonNeTargetTemplateFormSection | 'usnbdTargetInformation' | 'usnbdCallContentChannelInformation',
  targetSection?: TargetDeliveryTemplateInterfaceFormData[],
  data?: TargetDeliveryTemplateAccessFunctionFormData,
): TargetDeliveryTemplateSection[] {

  if (!targetSection || !data) {
    return [];
  }

  return targetSection
    .map((section, index) => ({
      id: `tab-${index}`,
      name,
      options: Object.keys(section)
        .filter((key) => filterGENBANDC20(key, name, data, index))
        .map((key) => ({ key, value: section[(key as keyof TargetDeliveryTemplateInterfaceFormData)] })),
    }));
}

const filterGENBANDC20 = (
  fieldKey: string,
  targetSection: 'targetInformation' | 'callContentChannels' | 'ICD' | 'networkType' | 'callContentType' | 'monitoringCenterFlags' | EricssonNeTargetTemplateFormSection | 'usnbdTargetInformation' | 'usnbdCallContentChannelInformation',
  data: TargetDeliveryTemplateAccessFunctionFormData,
  index: number,
) => {
  if (data.afType !== 'GENBANDC20') {
    return true;
  }

  const sectonData = data?.targetInformation?.[index];
  const enableRows = getEnabledRows(sectonData?.ACC as string, targetSection as 'targetInformation' | 'callContentChannels');

  return enableRows.includes(fieldKey);
};

function getEnabledRows(
  targetInformationAAA: string,
  section: 'targetInformation' | 'callContentChannels',
): string[] {

  const key = targetInformationAAA as ('IBN' | 'BRI' | 'PRI' | 'MFT' | 'VA' | 'HNT' | 'TRK');

  const newRows:string[][] = GENBAND_TARGET_CCC_ROWS?.[section]?.[key];

  if (newRows == null) {
    return [];
  }

  return newRows.reduce((total, actual) => [...total, ...actual], []);
}

function setFieldOptions(
  templateSection: FormTemplateSection,
  field: string,
  options: FormTemplateOption[],
  setFirstAsInitial?: boolean,
): void {
  if (setFirstAsInitial && templateSection?.fields?.[field]?.initial && options?.[0] != null) {
    templateSection.fields[field].initial = options[0].value;
  }

  templateSection.fields[field]?.options?.push(...options);
}

function getAfTypeTagOptions(
  templates: {[key: string]: FormTemplate},
  enabledCollectionFunctions: SupportedFeaturesAccessFunctions,
): FormTemplateOption[] {
  return Object.keys(templates).map((key) => ({
    label: enabledCollectionFunctions[key]?.title ?? templates[key].name,
    value: templates[key].key,
  }));
}

function getAfIdOptions(
  templates: {[key: string]: FormTemplate},
  accessFunctions: AccessFunction[],
): {[key:string]: FormTemplateOption[]} {

  return Object.keys(templates)
    .map((key) => {
      const filteredAccessFunction = accessFunctions.filter((af) => af.tag === key);

      return {
        [key]: filteredAccessFunction.map((af) => ({ label: af.name, value: (af.id as string) })),
      };
    })
    .reduce((total, current) => ({
      ...total,
      ...current,
    }), {});
}

function adaptTemplateForViewMode(template: FormTemplate) {
  Object.entries(template).forEach(([key, templateSection]) => {
    const { fields } = (templateSection as FormTemplateSection);
    if (fields != null) {
      Object.values(fields).forEach((field) => {
        field.readOnly = true;
      });
    }
  });

  return template;
}

function adaptTemplateForEditMode(template: FormTemplate) {
  (template.main as FormTemplateSection).fields.afType.readOnly = true;
  (template.main as FormTemplateSection).fields.afId.readOnly = true;

  return template;
}

export function getAuditDetailsToSubmit(
  dialedDigitExtractionServerData: TargetDeliveryTemplateAccessFunctionFormData,
  originalDigitExtractionServerFormData: TargetDeliveryTemplateInterfaceFormData | null,
  isAuditEnabled: boolean,
  userId: string,
  userName: string,
  operation: Operation,
): AuditDetails {

  let actionType = AuditActionType.TARGET_TEMPLATE_PER_ACCESS_FUNCTION_ADD;
  let isEdit = false;
  if (operation === 'UPDATE') {
    actionType = AuditActionType.TARGET_TEMPLATE_PER_ACCESS_FUNCTION_EDIT;
    isEdit = true;
  }

  const auditDetails = getAuditDetails(
    isAuditEnabled,
    userId,
    userName,
    getAuditFieldInfo(dialedDigitExtractionServerData, null, isEdit, originalDigitExtractionServerFormData),
    dialedDigitExtractionServerData.name,
    AuditService.TARGET_TEMPLATE_PER_ACCESS_FUNCTION,
    actionType,
  );
  return auditDetails as AuditDetails;
}
